/**
 * Turns the three scorecard payloads into the rows and columns the crosstab
 * renders. Every decision about what a cell says, how it is colored and what
 * its tooltip reads lives here; the components only render what this returns.
 *
 * Inputs come straight from the API, unmodified:
 *   config           GET /api/wq-scorecard/config/:scorecardKeys
 *   stats            GET /api/wq-scorecard/stats/:scorecardKeys?period=...
 *   benchmarkColors  GET /api/public-map/benchmark-scale-colors
 *   flow             "alldata" | "baseflow" | "stormflow"
 *   statistic        "median" | "pctile"
 *   parameterIds     optional whitelist of columns to show; omit for all
 *
 * Two scorecards can arrive in one payload (watershed and reservoir). Rows are
 * laid out by reach regardless of card. Columns are identified by parameter
 * AND metric: the reservoir card carries TP both as a water-year median on the
 * stream bands (metric "por") and as a July to September mean judged against
 * a regulatory standard (metric "summer_mean"), and those are two columns.
 *
 * A cell comes in four kinds:
 *   data            a number, shaded (or deliberately unshaded)
 *   no-data         the site is monitored for this, but not in this period: n/a
 *   never-sampled   the site has never been sampled for this: a dash
 *   not-applicable  the column belongs to the other scorecard: a dash
 */

import {
  ATTAINMENT_LABELS,
  BAND_LABELS,
  CELL_NOTES,
  SCALE_NOTES,
  UNSHADED_LABEL,
} from "./scorecardCopy";

// Fewer than this many samples and the cell is marked as thin. Six is one
// sample a month for half a year, below which a median says little.
export const SMALL_N_THRESHOLD = 6;

// The scale value data.list_benchmark_colors uses for "no benchmark defined".
const NO_BENCHMARK_SCALE = -1;

// Which of the five band colors an attainment outcome borrows, so the
// standards legend and the benchmark legend share one palette.
const ATTAINMENT_SCALE = {
  attaining: 1,
  not_attaining: 4,
  insufficient: NO_BENCHMARK_SCALE,
};

export const METRIC_POR = "por";
export const METRIC_SUMMER_MEAN = "summer_mean";

const FLOW_LABELS = {
  alldata: "All data",
  baseflow: "Baseflow",
  stormflow: "Stormflow",
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Mann-Kendall evaluations, as data_analysis.build02_mannkendall_trends writes
// them, plus the "No Current Trend" the map substitutes when a combination has
// four or fewer records.
const TREND_META = {
  increasing: {
    icon: "arrow_upward",
    label: "Increasing",
    description: "Increasing (95% confidence)",
  },
  "probably increasing": {
    icon: "trending_up",
    label: "Probably increasing",
    description: "Probably increasing (90% confidence)",
  },
  stable: {
    icon: "trending_flat",
    label: "Stable",
    description: "Stable (no significant change, low variability)",
  },
  "no trend": {
    icon: "swap_vert",
    label: "No trend",
    description: "No trend (no significant change, high variability)",
  },
  "probably decreasing": {
    icon: "trending_down",
    label: "Probably decreasing",
    description: "Probably decreasing (90% confidence)",
  },
  decreasing: {
    icon: "arrow_downward",
    label: "Decreasing",
    description: "Decreasing (95% confidence)",
  },
  "no current trend": {
    icon: "not_interested",
    label: "Not enough data",
    description: "Not enough data for a trend (five or more samples needed)",
  },
};

const UNKNOWN_TREND = {
  icon: "not_interested",
  label: "Not available",
  description: "No trend available",
};

export function getTrendMeta(trendEval) {
  if (!trendEval) return UNKNOWN_TREND;
  return TREND_META[String(trendEval).toLowerCase()] ?? UNKNOWN_TREND;
}

/**
 * Nutrients are reported in ug/L and read as whole numbers; everything else
 * gets one decimal. Mirrors how the map popover prints the same values.
 */
export function formatValue(value, unitName) {
  if (value === null || value === undefined || Number.isNaN(value)) return null;
  const digits = String(unitName).toLowerCase() === "ug/l" ? 0 : 1;
  return Number(value).toFixed(digits);
}

function formatDate(value) {
  if (!value) return null;
  // The API sends date-only strings; splitting beats constructing a Date,
  // which would shift the day in timezones behind UTC.
  return String(value).slice(0, 10);
}

// viewer_endpoints.trends_end_date holds a sentinel far in the future
// (2300-12-31) meaning "no upper bound". Printing that literally in a tooltip
// reads as a data error, so any date past this year is shown as "present".
const SENTINEL_YEAR = 2100;

export function formatWindowDate(value) {
  const date = formatDate(value);
  if (!date) return null;
  return Number(date.slice(0, 4)) >= SENTINEL_YEAR ? "present" : date;
}

function formatRange(start, end) {
  const from = formatWindowDate(start);
  const to = formatWindowDate(end);
  if (!from && !to) return null;
  return `${from ?? "?"} to ${to ?? "?"}`;
}

const unitSuffix = (units) => (units && units !== "None" ? ` ${units}` : "");

/**
 * The identity of a column. A plain parameter id for the ordinary metric, so
 * existing callers (and the URL's `params` list) keep working; parameter and
 * metric for anything else.
 */
export function columnKey(parameterid, metric) {
  return metric && metric !== METRIC_POR
    ? `${parameterid}:${metric}`
    : String(parameterid);
}

const metricOf = (row) => row?.metric ?? METRIC_POR;

function statsKey(locationid, parameterid, metric, flow) {
  return `${locationid}|${parameterid}|${metric}|${flow}`;
}

function cellConfigKey(locationid, parameterid, metric) {
  return `${locationid}|${parameterid}|${metric}`;
}

/**
 * Which number the cell shows. The percentile column holds the 15th rather
 * than the 85th whenever the parameter is one where low values are the
 * problem, and for pH parameters whose low tail bands worse than the high
 * one — exactly the swap viewer_endpoints.locations_map_graph_mode makes.
 */
function pickStatistic(row, statistic) {
  if (statistic === "median") {
    return {
      value: row.median,
      scale: row.bmk_scale_median,
      band: row.bmk_value_median,
    };
  }
  const value = row.ph_use_pctile15 ? row.pctile15 : row.pctile85;
  return { value, scale: row.bmk_scale_pctile, band: row.bmk_value_pctile };
}

// Parameters whose benchmark folds both tails onto one scale, per the CASE in
// data_analysis.stats_with_benchmarks_alldata.
const PH_SCALE_PARAMETERS = new Set([7, 20]);

/** "normal" (high is worse), "low" (low is worse) or "ph" (both tails). */
export function getScaleKind(parameterid, lines) {
  if (PH_SCALE_PARAMETERS.has(Number(parameterid))) return "ph";
  return lines && lines.is_pctile15 ? "low" : "normal";
}

/**
 * The numeric range the shading came from, e.g. "170 to 250 ug/L". Derived from
 * the value rather than the stored band so it works for Live too, where the raw
 * band is not available. bmk_line0..3 are the upper bounds of bands 0..3;
 * anything above bmk_line3 is the top band.
 */
export function getBandRange(value, lines, units, scaleKind) {
  if (!lines || value === null || value === undefined) return null;
  const edges = [
    lines.bmk_line0,
    lines.bmk_line1,
    lines.bmk_line2,
    lines.bmk_line3,
  ];
  if (edges.some((e) => e === null || e === undefined)) return null;

  const suffix = unitSuffix(units);
  const index = edges.findIndex((edge) => Number(value) <= Number(edge));

  if (index === -1) return `above ${edges[3]}${suffix}`;
  if (index === 0) {
    // For an ordinary parameter the bottom band really does mean non-detect;
    // for pH or dissolved oxygen it is simply the lowest range.
    return scaleKind === "normal"
      ? "not detected"
      : `at or below ${edges[0]}${suffix}`;
  }
  return `${edges[index - 1]} to ${edges[index]}${suffix}`;
}

export function getStatisticLabel(statistic, columns) {
  if (statistic === "median") return "Median";
  // Only call the whole table a 15th percentile when every column on it is one
  // of the low-is-bad parameters; otherwise the header would lie about the
  // majority of the columns. Same compromise the map makes.
  const ordinary = columns.filter((c) => !c.isStandard);
  const everyColumnIsLowIsBad =
    ordinary.length > 0 && ordinary.every((c) => c.isPctile15);
  return everyColumnIsLowIsBad ? "15th percentile" : "85th percentile";
}

/** "July to September", from the standard's season months. */
function seasonLabel(standard) {
  const from = MONTH_NAMES[(standard?.seasonStartMonth ?? 7) - 1];
  const to = MONTH_NAMES[(standard?.seasonEndMonth ?? 9) - 1];
  return `${from} to ${to}`;
}

/** The standard attached to a summer_mean config row, or null. */
function standardOf(c) {
  if (c.standard_value === null || c.standard_value === undefined) return null;
  return {
    value: Number(c.standard_value),
    basis: c.standard_basis ?? null,
    isInterim: Boolean(c.standard_is_interim),
    effectiveFrom: c.standard_effective_from ?? null,
    seasonStartMonth: c.standard_season_start_month ?? 7,
    seasonEndMonth: c.standard_season_end_month ?? 9,
    allowedExceedances: c.standard_allowed_exceedances ?? 1,
    ofYears: c.standard_of_years ?? 5,
    minSamples: c.standard_min_samples ?? 3,
  };
}

export function buildScorecardTable({
  config = [],
  stats = [],
  benchmarkColors = [],
  flow = "alldata",
  statistic = "median",
  parameterIds = null,
  benchmarkLines = [],
} = {}) {
  const linesByParameter = new Map(
    (benchmarkLines || []).map((l) => [Number(l.parameter_index), l])
  );
  // null or an empty selection means every column; anything else is a whitelist.
  const wantedParameters =
    Array.isArray(parameterIds) && parameterIds.length
      ? new Set(parameterIds.map(Number))
      : null;
  const colorByScale = new Map(
    benchmarkColors.map((c) => [Number(c.bmk_value), c])
  );
  const statsByKey = new Map(
    stats
      .filter((s) => s.flow_condition === flow)
      .map((s) => [
        statsKey(s.locationid, s.parameterid, metricOf(s), s.flow_condition),
        s,
      ])
  );

  const sortedConfig = [...config].sort(
    (a, b) =>
      a.reach_order - b.reach_order ||
      a.site_order - b.site_order ||
      a.parameter_order - b.parameter_order
  );

  // Every config row, by cell, so a cell can read its own shading, flow and
  // sampling history.
  const configByCell = new Map();
  sortedConfig.forEach((c) => {
    configByCell.set(
      cellConfigKey(c.locationid, c.parameterid, metricOf(c)),
      c
    );
  });

  // Columns: one per parameter-and-metric, in the order scorecard_config
  // gives them. Standard columns are never filtered by the parameter picker:
  // they come and go with the reservoir rows they belong to.
  const columnByKey = new Map();
  sortedConfig.forEach((c) => {
    const metric = metricOf(c);
    const key = columnKey(c.parameterid, metric);
    const isStandard = metric === METRIC_SUMMER_MEAN;
    const existing = columnByKey.get(key);
    if (existing) {
      existing.scorecardKeys.add(c.scorecard_key);
      if (!existing.standard) existing.standard = standardOf(c);
      return;
    }
    if (
      !isStandard &&
      wantedParameters &&
      !wantedParameters.has(Number(c.parameterid))
    )
      return;
    const anyStat = stats.find(
      (s) => s.parameterid === c.parameterid && metricOf(s) === metric
    );
    const lines = linesByParameter.get(Number(c.parameterid)) ?? null;
    const scaleKind = getScaleKind(c.parameterid, lines);
    columnByKey.set(key, {
      key,
      parameterid: c.parameterid,
      metric,
      isStandard,
      standard: standardOf(c),
      abbrev: c.param_abbrev,
      description: c.param_descrip,
      units: c.unit_name,
      order: c.parameter_order,
      isPctile15: Boolean(anyStat && anyStat.is_pctile15),
      scaleKind,
      // Only the exceptions are annotated; the usual case needs no note.
      scaleNote: isStandard ? null : SCALE_NOTES[scaleKind] ?? null,
      lines: isStandard ? null : lines,
      scorecardKeys: new Set([c.scorecard_key]),
    });
  });
  const columns = [...columnByKey.values()].sort(
    (a, b) => Number(b.isStandard) - Number(a.isStandard) || a.order - b.order
  );

  // Every ordinary column the configuration offers, hidden or not, so the
  // picker can list the full set while the table renders only the chosen ones.
  const allParameters = [];
  const seenAll = new Set();
  sortedConfig.forEach((c) => {
    if (metricOf(c) !== METRIC_POR) return;
    if (seenAll.has(c.parameterid)) return;
    seenAll.add(c.parameterid);
    allParameters.push({
      parameterid: c.parameterid,
      abbrev: c.param_abbrev,
      description: c.param_descrip,
      units: c.unit_name,
      group: c.param_group ?? "Other",
      order: c.parameter_order,
    });
  });
  allParameters.sort((a, b) => a.order - b.order);

  const statisticLabel = getStatisticLabel(statistic, columns);
  const flowLabel = FLOW_LABELS[flow] ?? flow;

  // Rows: one per site, grouped under a reach header. A site appears once even
  // though config carries a row per site-and-parameter pair.
  const rows = [];
  const rowByLocation = new Map();

  sortedConfig.forEach((c) => {
    const existing = rowByLocation.get(c.locationid);
    if (existing) {
      if (c.flow_applies === false) existing.flowApplies = false;
      if (c.is_composite) existing.isComposite = true;
      return;
    }
    const row = {
      locationid: c.locationid,
      scorecardKey: c.scorecard_key,
      siteName: c.location_name,
      siteDescription: c.location_desc,
      reachLabel: c.reach_label,
      reachOrder: c.reach_order,
      siteOrder: c.site_order,
      lat: c.loc_lat ?? null,
      lon: c.loc_lon ?? null,
      flowApplies: c.flow_applies !== false,
      isComposite: Boolean(c.is_composite),
      cells: {},
    };
    rowByLocation.set(c.locationid, row);
    rows.push(row);
  });

  rows.forEach((row) => {
    columns.forEach((column) => {
      const cfg = configByCell.get(
        cellConfigKey(row.locationid, column.parameterid, column.metric)
      );
      row.cells[column.key] = buildCell({
        row,
        column,
        cfg,
        stat: statsByKey.get(
          statsKey(row.locationid, column.parameterid, column.metric, flow)
        ),
        statistic,
        statisticLabel,
        flowLabel,
        colorByScale,
      });
    });
  });

  return {
    columns,
    rows,
    allParameters,
    statisticLabel,
    flowLabel,
    hasStandardColumns: columns.some((c) => c.isStandard),
    hasReservoirRows: rows.some((r) => !r.flowApplies || r.isComposite),
    hasUnshadedCells: rows.some((r) =>
      Object.values(r.cells).some((cell) => cell.isUnshaded)
    ),
  };
}

/** The lines every tooltip prints about the trend behind the arrow. */
function trendLines(stat, trend) {
  const lines = [`Trend: ${trend.description}`];

  const trendWindow = formatRange(stat.trend_start, stat.trend_end);
  if (trendWindow) lines.push(`Trend period: ${trendWindow}`);

  // Live has no trend statistics to report; a snapshot does. The sampled span
  // matters on sparse parameters, where eight samples over four years can sit
  // inside a thirty-year window.
  if (stat.trend_recordcount !== null && stat.trend_recordcount !== undefined) {
    const span = formatRange(stat.trend_por_start, stat.trend_por_end);
    lines.push(
      `Trend samples: ${stat.trend_recordcount}${
        span ? ` (sampled ${span})` : ""
      }`
    );
  }
  if (stat.trend_z !== null && stat.trend_z !== undefined) {
    lines.push(`Mann-Kendall z: ${Number(stat.trend_z).toFixed(2)}`);
  }
  if (stat.trend_cv !== null && stat.trend_cv !== undefined) {
    lines.push(`Coefficient of variation: ${Number(stat.trend_cv).toFixed(2)}`);
  }
  if (
    stat.trend_pct_nondetect !== null &&
    stat.trend_pct_nondetect !== undefined
  ) {
    lines.push(
      `Non-detects: ${Number(stat.trend_pct_nondetect).toFixed(
        0
      )}% of trend samples`
    );
  }
  return lines;
}

function flowLine(row, flowLabel) {
  return row.flowApplies
    ? `Flow condition: ${flowLabel}`
    : `Flow condition: not applicable (${CELL_NOTES.flowNotApplicable})`;
}

function emptyCell({ row, column, kind, display, srNote, title, lines }) {
  return {
    locationid: row.locationid,
    parameterid: column.parameterid,
    columnKey: column.key,
    metric: column.metric,
    siteName: row.siteName,
    parameterName: column.description,
    kind,
    hasData: false,
    display,
    value: null,
    recordcount: 0,
    isSmallSample: false,
    isUnshaded: false,
    backgroundColor: null,
    bandScale: NO_BENCHMARK_SCALE,
    bandLabel: BAND_LABELS["-1"],
    trend: UNKNOWN_TREND,
    srText: `${row.siteName}, ${column.description}: ${srNote}`,
    tooltip: { title, lines },
  };
}

function buildCell({
  row,
  column,
  cfg,
  stat,
  statistic,
  statisticLabel,
  flowLabel,
  colorByScale,
}) {
  const title = `${row.siteName} — ${column.description}`;
  const noBenchmarkColor = colorByScale.get(NO_BENCHMARK_SCALE);

  // The column belongs to the other scorecard: a regulatory column on a stream
  // row, or a reservoir-only parameter on a stream row.
  if (!cfg) {
    const note = column.isStandard
      ? CELL_NOTES.standardColumnReservoirOnly
      : CELL_NOTES.notOnThisScorecard;
    return emptyCell({
      row,
      column,
      kind: "not-applicable",
      display: "—",
      srNote: note,
      title,
      lines: [note],
    });
  }

  // Monitored, but not in this window, or never monitored at all. The config
  // view's last_sample_date tells the two apart; a payload that lacks the
  // field (older API, tests) falls back to plain n/a.
  if (!stat || !stat.recordcount) {
    if (cfg.last_sample_date === null) {
      return emptyCell({
        row,
        column,
        kind: "never-sampled",
        display: "—",
        srNote: CELL_NOTES.neverSampled,
        title,
        lines: [CELL_NOTES.neverSampled],
      });
    }
    const lastSampled = formatDate(cfg.last_sample_date);
    return {
      ...emptyCell({
        row,
        column,
        kind: "no-data",
        display: "n/a",
        srNote: `no data for ${flowLabel.toLowerCase()} in this period`,
        title,
        lines: [
          `${column.isStandard ? "Summer mean" : statisticLabel}: no data`,
          flowLine(row, flowLabel),
          stat
            ? `Period: ${formatRange(stat.stats_start, stat.stats_end)}`
            : null,
          CELL_NOTES.noDataThisPeriod,
          lastSampled ? `Last sampled: ${lastSampled}` : null,
        ].filter(Boolean),
      }),
      recordcount: stat ? stat.recordcount ?? 0 : 0,
      backgroundColor: noBenchmarkColor?.graph_background_color ?? null,
      trend: getTrendMeta(stat?.trend_eval),
    };
  }

  return column.isStandard
    ? buildStandardCell({ row, column, stat, flowLabel, colorByScale, title })
    : buildBenchmarkCell({
        row,
        column,
        cfg,
        stat,
        statistic,
        statisticLabel,
        flowLabel,
        colorByScale,
        title,
      });
}

/** A water-year statistic on the benchmark bands, or deliberately unshaded. */
function buildBenchmarkCell({
  row,
  column,
  cfg,
  stat,
  statistic,
  statisticLabel,
  flowLabel,
  colorByScale,
  title,
}) {
  const noBenchmarkColor = colorByScale.get(NO_BENCHMARK_SCALE);
  const isUnshaded = cfg.shading === "none";
  const { value, scale, band } = pickStatistic(stat, statistic);
  const color = isUnshaded
    ? null
    : colorByScale.get(Number(scale)) ?? noBenchmarkColor;
  const display = formatValue(value, column.units);
  const trend = getTrendMeta(stat.trend_eval);
  const isSmallSample = stat.recordcount < SMALL_N_THRESHOLD;
  // The stored legend_text is directional and therefore wrong for inverted
  // scales; see BAND_LABELS. The numeric range is always unambiguous.
  const bandLabel = isUnshaded
    ? UNSHADED_LABEL
    : BAND_LABELS[String(scale)] ?? BAND_LABELS["-1"];
  const bandRange = isUnshaded
    ? null
    : getBandRange(value, column.lines, column.units, column.scaleKind);

  const lines = [
    `${statisticLabel}: ${display ?? "n/a"}${unitSuffix(column.units)}`,
    isUnshaded
      ? `Benchmark: ${CELL_NOTES.unshaded}`
      : `Benchmark: ${bandLabel}${bandRange ? ` (${bandRange})` : ""}`,
    flowLine(row, flowLabel),
    `Samples: ${stat.recordcount}${isSmallSample ? " (small sample)" : ""}`,
    `Period: ${formatRange(stat.stats_start, stat.stats_end)}`,
  ];

  const sampled = formatRange(stat.por_start, stat.por_end);
  if (sampled) lines.push(`Sampled: ${sampled}`);

  lines.push(...trendLines(stat, trend));

  if (row.isComposite) lines.push(CELL_NOTES.composite);
  if (isSmallSample) lines.push(CELL_NOTES.smallSample);

  return {
    locationid: row.locationid,
    parameterid: column.parameterid,
    columnKey: column.key,
    metric: column.metric,
    siteName: row.siteName,
    parameterName: column.description,
    kind: "data",
    hasData: true,
    display,
    value,
    units: column.units,
    recordcount: stat.recordcount,
    isSmallSample,
    isUnshaded,
    backgroundColor: color?.graph_background_color ?? null,
    dotColor: color?.dot_symbol_color ?? null,
    bandScale: isUnshaded ? NO_BENCHMARK_SCALE : Number(scale),
    bandValue: isUnshaded ? null : band ?? null,
    bandLabel,
    bandRange,
    trend,
    statsStart: formatDate(stat.stats_start),
    statsEnd: formatDate(stat.stats_end),
    // One sentence carrying everything a screen reader needs: the number, the
    // band it falls in and the trend. The color alone never conveys the band.
    srText:
      `${row.siteName}, ${
        column.description
      }: ${statisticLabel.toLowerCase()} ` +
      `${display ?? "not available"}${unitSuffix(column.units)}, ` +
      `${bandLabel.toLowerCase()}, trend ${trend.label.toLowerCase()}, ` +
      `${stat.recordcount} sample${stat.recordcount === 1 ? "" : "s"}` +
      `${isSmallSample ? ", small sample" : ""}`,
    tooltip: { title, lines },
  };
}

/**
 * A July to September seasonal mean judged against a regulatory standard. The
 * number is the mean of the period's seasonal means; the color is whether the
 * period attains: at most one failing season in five, scaled to the seasons
 * on hand. Each season's own verdict is listed in the tooltip.
 */
function buildStandardCell({
  row,
  column,
  stat,
  flowLabel,
  colorByScale,
  title,
}) {
  const standard = column.standard;
  const value = stat.mean_val ?? stat.median;
  const display = formatValue(value, column.units);
  const trend = getTrendMeta(stat.trend_eval);
  const attainment = stat.attainment ?? "insufficient";
  const attainmentLabel =
    ATTAINMENT_LABELS[attainment] ?? ATTAINMENT_LABELS.insufficient;
  const scale = ATTAINMENT_SCALE[attainment] ?? NO_BENCHMARK_SCALE;
  const color = colorByScale.get(scale) ?? colorByScale.get(NO_BENCHMARK_SCALE);
  const isSmallSample = stat.recordcount < SMALL_N_THRESHOLD;
  const seasonsTotal = stat.seasons_total ?? 0;
  const seasonsMet = stat.seasons_met ?? 0;
  const standardValue = stat.standard_value ?? standard?.value ?? null;
  const allowed =
    standard && seasonsTotal
      ? Math.floor(
          (seasonsTotal * standard.allowedExceedances) / standard.ofYears
        )
      : 0;
  const season = seasonLabel(standard);
  const detail = Array.isArray(stat.season_detail) ? stat.season_detail : [];

  const lines = [
    `Summer mean: ${display ?? "n/a"}${unitSuffix(column.units)} (${season}, ${
      detail.length || seasonsTotal
    } season${(detail.length || seasonsTotal) === 1 ? "" : "s"})`,
    standardValue !== null
      ? `Standard: ${formatValue(standardValue, column.units)}${unitSuffix(
          column.units
        )}${standard?.isInterim ? " (interim table value)" : ""}`
      : null,
    `Assessment: ${attainmentLabel}`,
    seasonsTotal
      ? `Seasons meeting the standard: ${seasonsMet} of ${seasonsTotal} (${allowed} allowed to exceed)`
      : CELL_NOTES.noJudgedSeasons,
    stat.seasons_insufficient
      ? `Seasons with too few samples to judge: ${stat.seasons_insufficient}`
      : null,
  ].filter(Boolean);

  // One line per verdict, years listed, rather than one line per season: the
  // full record holds thirty-odd summers and a tooltip that long runs off the
  // screen. The season-by-season table is behind the cell's detail button.
  const seasons = detail.map((d) => ({
    year: d.year,
    mean: d.mean,
    display: formatValue(d.mean, column.units),
    n: d.n,
    judged: Boolean(d.judged),
    met: Boolean(d.met),
    verdict: !d.judged ? "too few samples" : d.met ? "met" : "exceeded",
  }));
  const yearsOf = (predicate) =>
    seasons
      .filter(predicate)
      .map((s) => s.year)
      .join(", ");
  if (seasons.length) {
    const met = yearsOf((s) => s.judged && s.met);
    const exceeded = yearsOf((s) => s.judged && !s.met);
    const thin = yearsOf((s) => !s.judged);
    if (met) lines.push(`Met: ${met}`);
    if (exceeded) lines.push(`Exceeded: ${exceeded}`);
    if (thin) lines.push(`Too few samples: ${thin}`);
    lines.push(CELL_NOTES.seasonDetailHint);
  }

  lines.push(
    flowLine(row, flowLabel),
    `Samples: ${stat.recordcount}${isSmallSample ? " (small sample)" : ""}`,
    `Period: ${formatRange(stat.stats_start, stat.stats_end)}`
  );
  const sampled = formatRange(stat.por_start, stat.por_end);
  if (sampled) lines.push(`Sampled: ${sampled}`);
  lines.push(...trendLines(stat, trend));
  if (row.isComposite) lines.push(CELL_NOTES.composite);
  if (standard?.basis) lines.push(standard.basis);

  return {
    locationid: row.locationid,
    parameterid: column.parameterid,
    columnKey: column.key,
    metric: column.metric,
    siteName: row.siteName,
    parameterName: column.description,
    kind: "data",
    hasData: true,
    display,
    value,
    units: column.units,
    recordcount: stat.recordcount,
    isSmallSample,
    isUnshaded: false,
    backgroundColor: color?.graph_background_color ?? null,
    dotColor: color?.dot_symbol_color ?? null,
    bandScale: scale,
    bandValue: null,
    bandLabel: attainmentLabel,
    bandRange:
      standardValue !== null
        ? `standard ${formatValue(standardValue, column.units)}${unitSuffix(
            column.units
          )}`
        : null,
    attainment,
    seasonsTotal,
    seasonsMet,
    // Every season in the period, for the detail popover.
    seasons,
    standardValue,
    seasonLabel: season,
    trend,
    statsStart: formatDate(stat.stats_start),
    statsEnd: formatDate(stat.stats_end),
    srText:
      `${row.siteName}, ${column.description}: summer mean ` +
      `${display ?? "not available"}${unitSuffix(column.units)}, ` +
      `${attainmentLabel.toLowerCase()}, ${seasonsMet} of ${seasonsTotal} seasons met the standard, ` +
      `trend ${trend.label.toLowerCase()}`,
    tooltip: { title, lines },
  };
}

export default buildScorecardTable;
